/**
 * A reply that leaked the model's own tool calls as text (AIVA-8875).
 *
 * Reported verbatim, from a Word chat that ran a cross-reference check, /improve
 * and a typed "reverify" in a row:
 *
 *   Clause 21.4 functions.word_get_selection_context ղարձել
 *   to=functions.pill_word_replace_selection 亂倫 彩票平台招商 盈立_json {"text":"Clause 21.4"}
 *
 * The model wrote its tool calls out instead of making them. The backend now
 * replaces such a reply before `done` (shared/services/garbled_reply.py, the
 * `reply_replaced` event); this module is the add-in's backstop so a garbled
 * reply is never SHOWN, nor offered as Insert / Replace / Copy, even if one
 * reaches the add-in — mid-stream, from an older backend, or from a thread
 * saved before the fix. Keep the pattern and the fallback text identical to the
 * backend's.
 */

/** Shown in place of a garbled reply. Identical to the backend's copy: a reply
 *  replaced there comes back with exactly this text after a reload, and is
 *  recognised by it to offer no buttons. */
export const GARBLED_REPLY_FALLBACK =
  "Sorry — something went wrong while I was writing that answer, so I've hidden it. " +
  "Please try again, or rephrase your request.";

// - `to=functions.<tool>`: the model's internal tool-addressing syntax.
// - `functions.<snake_case_tool>`: a tool name written out as text. Every tool
//   name has an underscore, so "functions." in ordinary prose never matches.
// - `<|word|>`: the special tokens that frame a model's internal channels.
const LEAK_PATTERN = /\bto=functions\.|\bfunctions\.[a-z][a-z0-9]*_[a-z0-9_]+|<\|[a-z_]+\|>/;

/** Whether `text` contains leaked tool-call or channel markup. */
export function isGarbledReply(text: string): boolean {
  return !!text && LEAK_PATTERN.test(text);
}

/** A reply that must offer no buttons: garbled, or already replaced. */
export function isUnusableReply(text: string): boolean {
  return text === GARBLED_REPLY_FALLBACK || isGarbledReply(text);
}

/**
 * What to show for `text`. Mid-stream, everything from the first leaked marker
 * on is held back — the backend replaces the whole reply at the end, so the
 * garble never flashes up first. Once settled, a garbled reply shows the
 * fallback in its place.
 */
export function displayableReply(text: string, streaming: boolean): string {
  if (!isGarbledReply(text)) return text;
  if (!streaming) return GARBLED_REPLY_FALLBACK;
  const at = text.search(LEAK_PATTERN);
  return text.slice(0, at).trimEnd();
}
